import {
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Chip,
  Container,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import UserActivityDashboard from "../../components/user-activities/UserActivityDashboard";
import { useGetEmployeeByIdQuery } from "../../store/services/userService";

const UserActivityPage: React.FC = () => {
  const { userId = "0" } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // Parse userId to number
  const userIdNum = parseInt(userId, 10);

  // Använd vår API för att hämta användardata
  const {
    data: user,
    isLoading: loading,
    error,
  } = useGetEmployeeByIdQuery(userIdNum, {
    skip: userIdNum <= 0,
  });

  useEffect(() => {
    if (userIdNum <= 0) {
      navigate("/users");
    }
  }, [userIdNum, navigate]);

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      {/* User Info Card */}
      <Paper
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
        }}
      >
        {error ? (
          <Alert
            severity="error"
            sx={{ display: "flex", alignItems: "center" }}
            icon={<ErrorIcon fontSize="large" />}
          >
            <Typography variant="subtitle1">
              Ett fel uppstod när användardata hämtades
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Försök igen senare eller kontakta support
            </Typography>
          </Alert>
        ) : loading ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              sx={{ mr: 3 }}
            />
            <Box>
              <Skeleton variant="text" width={200} height={30} />
              <Skeleton variant="text" width={150} height={20} />
              <Skeleton variant="text" width={250} height={20} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 4px 10px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                mr: 3,
              }}
              src={user?.ImagePath || ""}
            >
              {user?.FirstName?.charAt(0) || user?.Name?.charAt(0) || "U"}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="h5" component="h1">
                  {user?.Name ||
                    `${user?.FirstName} ${user?.LastName}` ||
                    "Okänd användare"}
                </Typography>
                <Chip
                  label={user?.Status ? "Aktiv" : "Inaktiv"}
                  size="small"
                  color={user?.Status ? "success" : "default"}
                  sx={{ ml: 1, height: 24 }}
                />
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Senast aktiv:{" "}
                {user?.LastUsed
                  ? new Date(user.LastUsed).toLocaleDateString("sv-SE")
                  : "Ingen data"}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Dashboard */}
      {!loading && !error && user && (
        <UserActivityDashboard
          userId={userIdNum}
          userName={
            user.Name || `${user.FirstName} ${user.LastName}` || "Användaren"
          }
        />
      )}
    </Container>
  );
};

export default UserActivityPage;
